// User Preferences Service
// API client for user-specific preference storage

import type { UserPreferences } from '@/types/preferences.types';
import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { getBackendHeaders } from './backendAuth';

// Use dedicated auth database URL (separate from chat storage)
/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 */
function getBackendUrl(): string {
  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[userPreferencesService] ⚠️ Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[userPreferencesService] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

interface UserPreferencesResponse {
  userId: number;
  preferences: UserPreferences;
  createdAt: Date;
  updatedAt: Date;
}

class UserPreferencesAPIService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    return getBackendHeaders();
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get user preferences from backend
   */
  async getPreferences(): Promise<UserPreferences> {
    const response = await fetch(`${getBackendUrl()}/user-preferences`, {
      headers: await this.getAuthHeaders(),
    });

    const data = await this.handleResponse<UserPreferencesResponse>(response);
    return data.preferences;
  }

  /**
   * Update user preferences (full replace)
   */
  async updatePreferences(preferences: UserPreferences): Promise<UserPreferences> {
    const response = await fetch(`${getBackendUrl()}/user-preferences`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ preferences }),
    });

    const data = await this.handleResponse<UserPreferencesResponse>(response);
    return data.preferences;
  }

  /**
   * Merge partial preferences (recommended)
   */
  async mergePreferences(partialPreferences: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await fetch(`${getBackendUrl()}/user-preferences`, {
      method: 'PATCH',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ preferences: partialPreferences }),
    });

    const data = await this.handleResponse<UserPreferencesResponse>(response);
    return data.preferences;
  }

  /**
   * Reset preferences to defaults
   */
  async resetPreferences(): Promise<UserPreferences> {
    const response = await fetch(`${getBackendUrl()}/user-preferences`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
    });

    const data = await this.handleResponse<UserPreferencesResponse>(response);
    return data.preferences;
  }

  /**
   * Check if backend is available
   */
  async isBackendAvailable(): Promise<boolean> {
    try {
      const response = await fetch(`${getBackendUrl().replace('/api', '')}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch {
      return false;
    }
  }
}

export const userPreferencesAPI = new UserPreferencesAPIService();
